import { useCallback, useEffect, useState, type FormEvent } from "react";
import logoLight from "./assets/alti2-logo-light.png";
import testBench from "./assets/test-bench.jpg";

/**
 * Customer video lookup.
 *
 * One field: the serial number off the back of the unit. One answer: the
 * inspection video that unit appears in, and which position in it is theirs —
 * a recording covers up to twelve units, at locations A through L.
 *
 * Nothing about anyone else's unit is shown, because the API never sends it.
 */

/** A real unit, used as the example anyone can try. */
const DEMO_SERIAL = "23009635";

type LookupResult = {
  serialNumber: string | null;
  location: string;
  passed: boolean | null;
  inspectedOn: string;
  inspectedBy: string | null;
  hasVideo: boolean;
  /** Set when the recording was published to YouTube. */
  embedUrl: string | null;
  /** Fallback for recordings filed on the server rather than published. */
  streamUrl: string | null;
};

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  /* The sheet carries a calendar date -- the day somebody wrote down -- stored
     as midnight UTC. Rendered in the reader's own zone it becomes the evening
     before, everywhere west of Greenwich: a unit inspected on the 24th was
     being shown to American customers as the 23rd. */
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** The brand mark as an instrument: a dial with the logo's broken ring, whose
 *  needle sweeps down through altitude once when the page opens. */
function AltimeterMark() {
  const ticks = Array.from({ length: 24 }, (_, i) => i * 15);
  return (
    <svg className="alt-dial" viewBox="0 0 200 200" aria-hidden="true" focusable="false">
      <circle className="alt-face" cx="100" cy="100" r="94" />
      {/* Broken ring, the way the logo breaks it. */}
      <circle className="alt-ring" cx="100" cy="100" r="86" />
      <g className="alt-ticks">
        {ticks.map((deg, i) => (
          <line
            key={deg}
            x1="100"
            y1={i % 2 === 0 ? 20 : 24}
            x2="100"
            y2="30"
            transform={`rotate(${deg} 100 100)`}
            strokeWidth={i % 2 === 0 ? 3 : 1.5}
          />
        ))}
      </g>
      <g className="alt-needle">
        <line x1="100" y1="104" x2="100" y2="38" />
        <circle cx="100" cy="100" r="7" />
      </g>
    </svg>
  );
}

export default function App() {
  const [serial, setSerial] = useState("");
  const [result, setResult] = useState<LookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /* The shop hands customers a link with their serial already in it, so a
     lookup has to be runnable without anybody typing or pressing anything. */
  const lookup = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`/api/public/video-lookup?serial=${encodeURIComponent(trimmed)}`);
      const body = await response.json();
      if (!response.ok) {
        setError(body?.error ?? "Something went wrong. Please try again.");
        return;
      }
      setResult(body as LookupResult);
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const search = (event: FormEvent) => {
    event.preventDefault();
    lookup(serial);
  };

  /* ?serial=26020123 opens straight to that unit. Read once, on arrival: after
     that the field belongs to whoever is typing in it. */
  useEffect(() => {
    const fromLink = new URLSearchParams(window.location.search).get("serial")?.trim();
    if (!fromLink) return;
    setSerial(fromLink);
    lookup(fromLink);
  }, [lookup]);

  return (
    <div className="page">
      <header className="header">
        <a className="brand" href="/" aria-label="Alti-2 Technologies">
          {/* The reversed artwork: the wordmark is black and would vanish here.
              Only the neutral ink was lifted to white -- the ring is untouched. */}
          <img src={logoLight} alt="Alti-2 Technologies" width={900} height={193} />
        </a>
        <p className="eyebrow">Quality Inspection</p>
        <h1>
          Watch your unit's <em>simulated skydive</em>
        </h1>
        <p className="lede">
          Every unit we build is flown on a simulated skydive and filmed before
          it ships. Enter the serial number printed on the back of yours to
          watch it.
        </p>
        <AltimeterMark />
      </header>

      <form className="search" onSubmit={search}>
        <label className="label" htmlFor="serial">
          Serial number
        </label>
        <div className="search-row">
          <input
            id="serial"
            className="input"
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            placeholder={`e.g. ${DEMO_SERIAL}`}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            enterKeyHint="search"
          />
          <button className="button" type="submit" disabled={loading || !serial.trim()}>
            {loading ? "Searching…" : "Find my video"}
          </button>
        </div>
        {/* Alti-2 has filmed for years; what is new is the lookup. Somebody
            with an older unit should be told that rather than left deciding
            they misread their own serial. */}
        <p className="hint">
          Recordings are published for units from serial <strong>26024030</strong>{" "}
          onward, and for some built earlier. Every unit was filmed — older
          recordings simply aren't online here yet.
        </p>
        {/* So the page can be tried by somebody who is not holding a unit. */}
        <button
          type="button"
          className="demo-link"
          onClick={() => { setSerial(DEMO_SERIAL); lookup(DEMO_SERIAL); }}
        >
          Don't have one to hand? Try {DEMO_SERIAL}
        </button>
      </form>

      {error && (
        <div className="notice notice-error" role="alert">
          {error}
        </div>
      )}

      {result && (
        <section className="result">
          <div className="result-head">
            {/* The one detail that makes a twelve-unit recording usable. */}
            <div className="box-dial" aria-hidden="true">
              <div>
                <span>{result.location}</span>
                <small>Box</small>
              </div>
            </div>
            <div className="result-id">
              <p className="result-label">Serial number</p>
              <p className="result-serial">{result.serialNumber}</p>
              {result.passed !== null && (
                <span className={result.passed ? "badge badge-pass" : "badge badge-fail"}>
                  {result.passed ? "Passed" : "Failed"}
                </span>
              )}
            </div>
          </div>

          <dl className="facts">
            <div className="fact">
              <dt>Box in video</dt>
              <dd>{result.location}</dd>
            </div>
            <div className="fact">
              <dt>Inspected on</dt>
              <dd>{formatDate(result.inspectedOn)}</dd>
            </div>
            {result.inspectedBy && (
              <div className="fact">
                <dt>Inspected by</dt>
                <dd>{result.inspectedBy}</dd>
              </div>
            )}
          </dl>

          {result.hasVideo ? (
            <>
              <p className="player-hint">
                This recording covers several units. Your unit is in box{" "}
                <strong>{result.location}</strong>.
              </p>
              {result.embedUrl ? (
                <div className="player-frame">
                  <iframe
                    className="player"
                    src={result.embedUrl}
                    title={`Inspection video for serial ${result.serialNumber ?? ""}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <video className="player" src={result.streamUrl ?? ""} controls preload="metadata" playsInline />
              )}
            </>
          ) : (
            <div className="notice notice-pending">
              This unit was inspected and its record is on file, but the recording
              has not been published yet. Please check back later.
            </div>
          )}
        </section>
      )}


      <section className="story">
        <figure className="story-photo">
          <img
            src={testBench}
            alt="Alti-2 test benches in DeLand, where every altimeter is tested before it ships"
            width={500}
            height={347}
            loading="lazy"
          />
        </figure>
        <div className="story-copy">
          <h2>
            Precision isn't something we promise.
            <br />
            <em>It's something we test for.</em>
          </h2>
          <p>
            Every Alti-2 altimeter — not a sample, but every single unit — goes
            through <strong>eleven individual tests</strong> before it ships. The
            last one is a full simulated skydive, run in a calibrated pressure
            test chamber that Alti-2 Technologies designed and built in DeLand
            for exactly this purpose.
          </p>
          <p className="story-kicker">
            A unit that doesn't read correctly through the entire jump doesn't
            leave the building.
          </p>
        </div>
      </section>
    </div>
  );
}
